import { useCallback, useEffect, useState, type PropsWithChildren } from 'react';

import { onboardingApi } from '@/core/api';
import type { OnboardingStatus } from '@/core/api';
import { useAuth } from '@/core/auth';

import { OnboardingContext } from './onboardingContextDef';

/**
 * Whether the signed-in tenant still owes onboarding.
 *
 * Fetched once per session rather than per route, because the gate lives in
 * `RequireAuth` and that runs on every navigation — a request per page change
 * would be one request per click for a fact that changes twice in an account's
 * lifetime.
 *
 * Deliberately fails **open**. If the status call errors, the user gets the
 * application rather than a locked door: a gate that cannot reach its server
 * and therefore admits nobody turns a brief outage into every tenant being
 * unable to work. The server still refuses anything they should not do.
 */
export function OnboardingProvider({ children }: PropsWithChildren) {
  const { isAuthenticated, currentUser } = useAuth();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setStatus(null);
      setLoading(false);
      return;
    }
    try {
      setStatus(await onboardingApi.getStatus());
    } catch {
      // Fail open — see above.
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setLoading(true);
    void refresh();
    // `currentUser` in the deps, not just `isAuthenticated`: choosing a role on
    // a multi-role account swaps the token without signing out, and the answer
    // is per-role — an admin has no onboarding, the provider they just became
    // may well have.
  }, [refresh, currentUser]);

  return (
    <OnboardingContext.Provider value={{ status, loading, refresh }}>
      {children}
    </OnboardingContext.Provider>
  );
}
